/**
 * TextEdit strict validators: only the members doc/classes/TextEdit.xml lists without
 * `overrides=`, since the base-walk delivers the rest. `focus_mode` and `mouse_default_cursor_shape`
 * carry `overrides="Control"`, and text_edit.cpp's `_bind_methods` never re-declares them.
 * Grouped as the ADD_GROUP structure at text_edit.cpp:7545-7608 groups them.
 */
import '../../../2d/ui/control/linterParser.js';
import { validatorRegistry } from '../../../../linter/ValidatorRegistry.js';
import { v } from '../../../../linter/validators/index.js';
import { TEXT_EDIT_CARET_TYPE_NAMES, TEXT_EDIT_LINE_WRAPPING_MODE_NAMES, } from '../../../../godot/scene/gui.js';
import { AutowrapMode, AUTOWRAP_MODE_NAMES, STRUCTURED_TEXT_PARSER_NAMES, TEXT_DIRECTION_NAMES, } from '../../../../godot/text/textServerEnums.js';
validatorRegistry.registerAll('TextEdit', {
    // Text & behaviour (ungrouped run, text_edit.cpp:7545-7561).
    // text_edit.cpp:7545: Variant::STRING, PROPERTY_HINT_MULTILINE_TEXT.
    text: v.quotedString('text'),
    // text_edit.cpp:7546: Variant::STRING, PROPERTY_HINT_MULTILINE_TEXT.
    placeholder_text: v.quotedString('placeholder_text'),
    // text_edit.cpp:7548
    editable: v.boolean('editable'),
    // text_edit.cpp:7549
    context_menu_enabled: v.boolean('context_menu_enabled'),
    // text_edit.cpp:7550
    emoji_menu_enabled: v.boolean('emoji_menu_enabled'),
    // text_edit.cpp:7551
    backspace_deletes_composite_character_enabled: v.boolean('backspace_deletes_composite_character_enabled'),
    // text_edit.cpp:7552
    shortcut_keys_enabled: v.boolean('shortcut_keys_enabled'),
    // text_edit.cpp:7553
    selecting_enabled: v.boolean('selecting_enabled'),
    // text_edit.cpp:7554
    deselect_on_focus_loss_enabled: v.boolean('deselect_on_focus_loss_enabled'),
    // text_edit.cpp:7555
    drag_and_drop_selection_enabled: v.boolean('drag_and_drop_selection_enabled'),
    // text_edit.cpp:7556
    middle_mouse_paste_enabled: v.boolean('middle_mouse_paste_enabled'),
    // text_edit.cpp:7557
    empty_selection_clipboard_enabled: v.boolean('empty_selection_clipboard_enabled'),
    // text_edit.cpp:7558: PROPERTY_HINT_ENUM "None,Boundary"; BIND_ENUM_CONSTANT
    // LINE_WRAPPING_NONE=0, LINE_WRAPPING_BOUNDARY=1 (text_edit.cpp:7402-7403,
    // enum declared text_edit.h:68-71). set_line_wrapping_mode (text_edit.cpp:6341-6351)
    // assigns unconditionally, no ERR_FAIL.
    wrap_mode: v.enumInt('wrap_mode', TEXT_EDIT_LINE_WRAPPING_MODE_NAMES, { hinted: 'text_edit.cpp:7558' }),
    // text_edit.cpp:7559: PROPERTY_HINT_ENUM "Arbitrary:1,Word:2,Word (Smart):3", so the bound is 1-3.
    // TextServer::AutowrapMode starts at AUTOWRAP_OFF=0 (servers/text/text_server.h:98-102,
    // BIND_ENUM_CONSTANT text_server.cpp:574-577), and set_autowrap_mode has no CLAMP or ERR_FAIL
    // (text_edit.cpp:6354-6360), so 0 warns rather than errors.
    autowrap_mode: v.enumInt('autowrap_mode', AutowrapMode.AUTOWRAP_ARBITRARY, AutowrapMode.AUTOWRAP_WORD_SMART, AUTOWRAP_MODE_NAMES, { hinted: 'text_edit.cpp:7559' }),
    // text_edit.cpp:7560
    indent_wrapped_lines: v.boolean('indent_wrapped_lines'),
    // text_edit.cpp:7561
    tab_input_mode: v.boolean('tab_input_mode'),
    // Virtual Keyboard (ADD_GROUP "Virtual Keyboard", text_edit.cpp:7563-7565).
    virtual_keyboard_enabled: v.boolean('virtual_keyboard_enabled'),
    virtual_keyboard_show_on_focus: v.boolean('virtual_keyboard_show_on_focus'),
    // Scroll (ADD_GROUP "Scroll", text_edit.cpp:7567-7574).
    scroll_smooth: v.boolean('scroll_smooth'),
    // text_edit.cpp:7569: TextEdit::set_v_scroll_speed ERR_FAIL_COND(p_speed < 1.0)
    // (text_edit.cpp:6490): a real file can never carry a value below 1.
    scroll_v_scroll_speed: v.float('scroll_v_scroll_speed', { min: 1, enforced: 'text_edit.cpp:6490' }),
    scroll_past_end_of_file: v.boolean('scroll_past_end_of_file'),
    // text_edit.cpp:7571: set_v_scroll (text_edit.cpp:6463-6470) forwards to the internal
    // VScrollBar's Range::set_value, and Range::_calc_value clamps `p_val < shared->min` unless
    // `allow_lesser` (scene/gui/range.cpp:196). TextEdit keeps the defaults (min=0.0,
    // allow_lesser=false, scene/gui/range.h:40,46), so no lesser value is ever saved.
    scroll_vertical: v.float('scroll_vertical', { min: 0, enforced: 'range.cpp:196' }),
    // text_edit.cpp:7572: TextEdit::set_h_scroll clamps p_scroll < 0 to 0
    // in-place (text_edit.cpp:6477-6479), so a saved scene never carries a
    // negative value.
    scroll_horizontal: v.int('scroll_horizontal', { min: 0, enforced: 'text_edit.cpp:6477' }),
    scroll_fit_content_height: v.boolean('scroll_fit_content_height'),
    scroll_fit_content_width: v.boolean('scroll_fit_content_width'),
    // Minimap (ADD_GROUP "Minimap", text_edit.cpp:7576-7578).
    minimap_draw: v.boolean('minimap_draw'),
    // text_edit.cpp:7578: TextEdit::set_minimap_width (text_edit.cpp:6690-6696)
    // stores the int with no clamp or ERR_FAIL, so any integer is engine-valid.
    minimap_width: v.int('minimap_width'),
    // Caret (ADD_GROUP "Caret", text_edit.cpp:7580-7587).
    // text_edit.cpp:7581: PROPERTY_HINT_ENUM "Line,Block", CARET_TYPE_LINE=0, CARET_TYPE_BLOCK=1
    // (text_edit.cpp:7293-7294, text_edit.h:53-56). set_caret_type (text_edit.cpp:5159-5166) has no ERR_FAIL.
    caret_type: v.enumInt('caret_type', TEXT_EDIT_CARET_TYPE_NAMES, { hinted: 'text_edit.cpp:7581' }),
    caret_blink: v.boolean('caret_blink'),
    // text_edit.cpp:7583 hints "0.1,10,0.01,suffix:s", closed both ends.
    // text_edit.cpp:5198, ERR_FAIL_COND(p_interval <= 0): the setter refuses at
    // 0, below the hint's own floor, and assigns anything above it straight
    // through, so (0, 0.1) and everything past 10 warn.
    caret_blink_interval: v.float('caret_blink_interval', {
        enforcedMin: { at: 0, exclusive: true },
        min: 0.1,
        max: 10,
        enforced: { min: 'text_edit.cpp:5198' },
        hinted: 'text_edit.cpp:7583',
    }),
    caret_draw_when_editable_disabled: v.boolean('caret_draw_when_editable_disabled'),
    caret_move_on_right_click: v.boolean('caret_move_on_right_click'),
    caret_mid_grapheme: v.boolean('caret_mid_grapheme'),
    caret_multiple: v.boolean('caret_multiple'),
    // Word Separators (ADD_GROUP "Word Separators", "", text_edit.cpp:7589-7592).
    use_default_word_separators: v.boolean('use_default_word_separators'),
    use_custom_word_separators: v.boolean('use_custom_word_separators'),
    // text_edit.cpp:7592: Variant::STRING, no MULTILINE hint.
    custom_word_separators: v.quotedString('custom_word_separators'),
    // Highlighting (ADD_GROUP "Highlighting", "", text_edit.cpp:7594-7597).
    // text_edit.cpp:7595: Variant::OBJECT, PROPERTY_HINT_RESOURCE_TYPE "SyntaxHighlighter".
    syntax_highlighter: v.resourceReference('syntax_highlighter'),
    highlight_all_occurrences: v.boolean('highlight_all_occurrences'),
    highlight_current_line: v.boolean('highlight_current_line'),
    // Visual Whitespace (ADD_GROUP "Visual Whitespace", "draw_", text_edit.cpp:7599-7602).
    draw_control_chars: v.boolean('draw_control_chars'),
    draw_tabs: v.boolean('draw_tabs'),
    draw_spaces: v.boolean('draw_spaces'),
    // BiDi (ADD_GROUP "BiDi", "", text_edit.cpp:7604-7608). text_edit.cpp:7605: PROPERTY_HINT_ENUM
    // "Auto,Left-to-Right,Right-to-Left,Inherited", AUTO=0 to INHERITED=3 (scene/gui/control.cpp:4415-4418,
    // control.h:166-171, as in servers/text/text_server.h:66-71). set_text_direction (text_edit.cpp:3724)
    // refuses below -1 or above 3. -1 is a legacy inherited spelling with no constant, outside the hint.
    text_direction: v.enumInt('text_direction', TEXT_DIRECTION_NAMES, {
        hinted: 'text_edit.cpp:7605',
        enforced: 'text_edit.cpp:3724',
        enforcedMin: { at: -1 },
    }),
    // text_edit.cpp:7606: Variant::STRING, PROPERTY_HINT_LOCALE_ID.
    language: v.quotedString('language'),
    // text_edit.cpp:7607: PROPERTY_HINT_ENUM "Default,URI,File,Email,List,None,Custom",
    // STRUCTURED_TEXT_DEFAULT=0 .. STRUCTURED_TEXT_CUSTOM=6 (servers/text/text_server.cpp:681-687,
    // text_server.h:214-222). set_structured_text_bidi_override (text_edit.cpp:3778-3786) has no ERR_FAIL.
    structured_text_bidi_override: v.enumInt('structured_text_bidi_override', STRUCTURED_TEXT_PARSER_NAMES, {
        hinted: 'text_edit.cpp:7607',
    }),
    // text_edit.cpp:7608: Variant::ARRAY with no hint, so never written wrapped.
    // The contents are opaque parser-specific arguments with no fixed arity or
    // element type; set_structured_text_bidi_override_options (text_edit.cpp:3793-3803)
    // assigns straight through, leaving only the literal shape to reject.
    structured_text_bidi_override_options: v.arrayLiteral('structured_text_bidi_override_options'),
});
//# sourceMappingURL=linterParser.js.map